
  <template>
    <tr>
      <td v-if="isSelectable">
        <b-checkbox type="checkbox" v-model="isSelected" />
      </td>
      <td class="transaction-number">
        <font-awesome-icon icon="spinner" spin v-show="isDeleting" />
        <span class="delete-btn" @click="toggleConfirmation" v-if="canDeleteTransactions && !isDeleting">
          <font-awesome-icon icon="times-circle"/>
        </span>
        <router-link :to="toLink">
          {{ companyJob.companyJobId || 'not-avilable-yet' }}
        </router-link>
      </td>

      <td>
        <span v-for="(origin, index) in origins" :key="index">
          {{ origin }}
          <br/>
        </span>
      </td>
      <td>
        <span v-for="(destination, index) in destinations" :key="index">
          {{ destination }}
          <br/>
        </span>
      </td>
      <td> {{ companyJob.companyJobStatus }} </td>

      <td class="column-milestones">
        <template v-for="(milestones) in milestoneStates">
          <b-tooltip :label="milestones.displayName" position="is-top" :key="milestones.key">
            <font-awesome-icon class="milestone-flag-indicator" icon="circle" :color="milestones.color"/>
          </b-tooltip>
        </template>
        <!--
        <template v-for="(successFlag) in milestoneProgressColorFlags">
          <b-tooltip :label="successFlag.displayName" position="is-top" :key="successFlag.display">
            <font-awesome-icon class="milestone-flag-indicator" icon="circle" :color="successFlag.color"/>
          </b-tooltip>
        </template>
        -->
      </td>
      <delete-confirmation :show.sync="showDeleteConfirmation" :company-job="companyJob" @deleteConfirmed="deleteTransaction"  />
    </tr>
  </template>

  <script>
import { orderBy, last } from 'lodash-es'
import { mapGetters, mapState, mapActions } from 'vuex'
import MILESTONES_TO_TRACK from '@/lookup-data/milestones-to-track'
import DeleteConfirmation from '@/components/TransactionDeleteConfirmationModal'

export default {
  name: 'TransactionsTable-row',

  props: {
    // TODO: this is supposed to be temporary until a legit "job-certified" execution log entry is put in place
    isJobCertified: {
      type: Boolean,
      default: true
    },
    companyJob: {
      type: Object,
      required: true
    },
    isSelectable: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isSelected: false,
      showDeleteConfirmation: false,
      isDeleting: false
    }
  },

  created() {
    this.isSelected = this.selected
  },

  computed: {
    ...mapGetters('user', ['accountInfo']),
    ...mapGetters('companyJobs', ['isInProgress', 'isShipperApprovalPending']),
    ...mapState('companyJobs', ['executionLogs']),

    toLink() {
      if (this.companyJob.companyJobStatus === 'Draft' || this.companyJob.companyJobStatus === 'Invites sent') {
        return { name: 'create-transaction', params: { companyJobKey: this.companyJob.companyJobKey } }
      } else {
        return { name: 'transactions-details', params: { companyJobKey: this.companyJob.companyJobKey } }
      }
    },

    canDeleteTransactions() {
      return (
        this.companyJob.companyJobStatus === 'Draft' ||
        this.companyJob.companyJobStatus === 'Invites sent' ||
        !this.companyJob.companyJobStatus
      )
    },

    origins() {
      return orderBy(this.companyJob.origins, 'companyJobPointDateEarliest', 'asc').map(
        (jobPoint) =>
          jobPoint.companyRolodexJobLocation ? jobPoint.companyRolodexJobLocation.companyRolodexJobLocationName : 'N/A'
      )
    },

    destinations() {
      return orderBy(this.companyJob.destinations, 'companyJobPointDateEarliest', 'asc').map(
        (jobPoint) =>
          jobPoint.companyRolodexJobLocation ? jobPoint.companyRolodexJobLocation.companyRolodexJobLocationName : 'N/A'
      )
    },

    /**
     * An array of all milestones with a flag to indicate if they have been executed or not.
     * The order in which tye were executed does not matter, all that matters is that the key appears in the execution
     * log.
     *
     * This is not intended to be used directly but is a support  mechanism to aid the calculation of `milestoneStates`
     */
    _allExecutedMilestones() {
      return MILESTONES_TO_TRACK.map((milestone) => {
        const wasExecuted = this.executedMilestoneKeys.includes(milestone.key)
        return {
          key: milestone.key,
          displayName: milestone.displayName,
          wasExecuted,
          color: wasExecuted ? 'green' : 'gray'
        }
      })
    },

    /**
     * An array of all milestones with a flag to indicate if they have been executed, skipped or neither.
     * Leverages `_allExecutedMilestones` and adds information about if a milestone was skipped or not.
     *
     */
    milestoneStates() {
      // get array of true/false flags... then find the LAST `true` entry.
      const wasExecutedFlags = this._allExecutedMilestones.map((milestone) => milestone.wasExecuted)
      const indexOfLastExecutedMilestone = wasExecutedFlags.lastIndexOf(true)
      // every index BEFORE `indexOfLastExecutedMilestone` should have been executed, otherwise they were skipped
      return this._allExecutedMilestones.map((milestone, index) => {
        let wasSkipped = index < indexOfLastExecutedMilestone && !milestone.wasExecuted

        if (index <= indexOfLastExecutedMilestone && milestone.key === 'finish_job' && !this.isJobCertified) {
          wasSkipped = true
        }

        return {
          ...milestone,
          wasSkipped,
          color: wasSkipped ? 'red' : milestone.color
        }
      })
    },

    currentCompanyKey() {
      return this.accountInfo.company.companyKey
    },

    /**
     * An array of milestone keys, in the order they are expected to show up in the execution log
     */
    milestoneKeyOrder() {
      return MILESTONES_TO_TRACK.map((milestone) => milestone.key)
    },

    /**
     * The execution log for this specific transaction
     */
    executionLog() {
      let value = []
      if (this.executionLogs.hasOwnProperty(this.companyJob.companyJobKey)) {
        value = this.executionLogs[this.companyJob.companyJobKey]
      }

      return value
    },

    executedMilestoneKeys() {
      return this.executionLog.map((entry) => entry.companyJobExecutionLogEntryKey)
    },

    /**
     * Like the names implies... the last entry from the execution log
     */
    lastLogEntry() {
      let value = ''
      if (this.executionLog.length) {
        value = last(this.executionLog)
      }
      return value
    },

    /**
     * The next milestone that we expect to see in the execution log
     */
    nextMilestone() {
      const current = this.lastLogEntry.companyJobExecutionLogEntryKey
      let value = 'N/A'
      if (current) {
        const nextIndex = this.milestoneKeyOrder.indexOf(current) + 1
        value = MILESTONES_TO_TRACK[nextIndex]
      }

      return value
    }
  },

  components: {
    DeleteConfirmation
  },

  methods: {
    ...mapActions('companyJobs', ['deleteByCompanyJobKey']),
    toggleConfirmation() {
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },
    async deleteTransaction() {
      this.toggleConfirmation()

      try {
        this.isDeleting = true
        await this.deleteByCompanyJobKey(this.companyJob.companyJobKey)
      } catch (error) {
        console.error(`Error deleting transaction [${this.companyJob.companyJobKey}]: `, error)
      } finally {
        this.isDeleting = false
      }
    }
  },

  watch: {
    isSelected() {
      this.$emit('selectionChange-row', { isSelected: this.isSelected, companyJobKey: this.companyJob.companyJobKey })
    }
  }
}
</script>

  <style scoped lang="scss">
.transaction-number {
  white-space: nowrap;
}

.delete-btn {
  cursor: pointer;

  &:hover {
    color: red;
  }
}

.milestone-flag-indicator {
  margin: 0 3px;
}

td {
  font-size: 0.85rem;
}

.column-milestones {
  min-width: 315px;
}
</style>
